/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileInfo from '../../../components/Users/ProfileInfo';
import Services from '../../../components/Users/Services';
import { userDetailsAPI } from '../../../redux/features/users/userSlice';
import { useAppDispatch } from '../../../redux/store';
import { ErrorModal, Spinner } from '../../../components/shared';
import { ProfileInfoTypes } from '../../../utils/types';
import { Tab, TabContent, Tabs, TabsList } from '../../../components/shared/Tabs';
import { BookingsList } from '../../../components/Users/Bookings';

export interface ServicesListTypes {
  index?: number;
  id: number | string;
  serviceName?: string;
  description?: string;
  price?: string;
  duration?: string;
  status?: Boolean;
}

const UserDetails: FC = () => {
  // REDUX
  const dispatch = useAppDispatch();
  const details = useLocation()?.state;
  const userDetails = details?.userDetails ?? null;
  // STATES
  const [profileInfo, setProfileInfo] = useState<ProfileInfoTypes | null>(null);
  const [servicesRowData, setServicesRowData] = useState<ServicesListTypes[]>([]);
  const [screenLoading, setScreenLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // GET USER DETAILS
  const getUserDetails = async () => {
    setScreenLoading(true);
    const response = await dispatch(userDetailsAPI({ id: userDetails?.id ?? null }));
    if (response?.meta?.requestStatus === 'fulfilled') {
      const profileDetails = response?.payload?.data?.profile;
      let servicesList = response?.payload?.data?.services || [];
      //SERVICES
      servicesList = servicesList.map((service: any, index: number) => {
        return {
          ...service,
          index: index + 1,
          id: service?._id,
          serviceName: service?.name ?? '',
          description: service?.description ?? '',
          price: String('$' + service?.price) ?? '$0',
          duration: Number(service?.duration) > 0 ? String(service?.duration + ' min') : '0',
          status: service?.status
        };
      });
      // PROFILE SETTING
      setProfileInfo({
        fullname: profileDetails
          ? `${profileDetails?.firstName ?? ''} ${profileDetails?.lastName ?? ''}`.trim() || 'N/A'
          : 'N/A',
        email: profileDetails?.email ?? userDetails?.email ?? 'N/A',
        phone: profileDetails?.phone ?? 'N/A',
        address: profileDetails?.address ?? 'N/A',
        state: profileDetails?.state ?? 'N/A',
        zipCode: profileDetails?.zipCode ?? 'N/A',
        licenseNumber: profileDetails?.licenseNumber ?? 'N/A',
        licenseImage: profileDetails?.licenseImage?.Location ?? '',
        profileImage:
          profileDetails?.profileImage?.Location ??
          userDetails?.profile?.profileImage?.Location ??
          '',
        _id: profileDetails?._id,
        userId: userDetails?.id
      });
      setServicesRowData(servicesList);
      setScreenLoading(false);
    }
    if (response?.meta?.requestStatus === 'rejected') {
      setScreenLoading(false);
      console.log('User Details Error:', response?.payload);
      setError(response?.payload as string);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  if (screenLoading) {
    return (
      <div className='w-full h-full flex justify-center items-center'>
        <div className='text-lg font-medium'>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-full w-full justify-center px-4 pt-4 flex flex-col gap-8'>
      {/* Profile Information */}
      <div>
        <h1 className='text-2xl font-medium pb-3'>Profile Information</h1>
        <ProfileInfo profileInfo={profileInfo} />
      </div>
      {/* Services */}
      <div>
        <Tabs defaultTab='services' className='w-full'>
          <TabsList className='border-b border-separate gap-2'>
            <Tab
              value='services'
              activeClassNames='!border-b-2 border-primary bg-primary/10 !text-black'
              className='  shadow-md !px-8 rounded-t-md transition-all duration-300 ease-in-out'
            >
              Services
            </Tab>
            <Tab
              value='bookings'
              activeClassNames='!border-b-2 border-primary bg-primary/10 !text-black'
              className='  shadow-md !px-8 rounded-t-md transition-all duration-300 ease-in-out'
            >
              Bookings
            </Tab>
          </TabsList>

          <TabContent value='services'>
            <Services servicesList={servicesRowData} />
          </TabContent>
          <TabContent value='bookings'>
            <BookingsList row={userDetails} />
          </TabContent>
        </Tabs>
      </div>

      <ErrorModal showModal={Boolean(error)} errorMsg={error} />
    </div>
  );
};

export default UserDetails;
